/* ==========================================================================
   Sahia Musc — comportements front
   Chaque module est autonome et sort immédiatement si son point d'ancrage
   est absent de la page.
   ========================================================================== */

(function () {
  'use strict';

  var routes = window.themeRoutes || {};
  var strings = window.themeStrings || {};
  var WISHLIST_KEY = 'sahia:wishlist';

  /* ---------------------------------------------------------------------- */
  /* Utilitaires                                                            */
  /* ---------------------------------------------------------------------- */

  function on(selector, event, handler, root) {
    var scope = root || document;
    scope.querySelectorAll(selector).forEach(function (el) {
      el.addEventListener(event, handler);
    });
  }

  function trapFocus(container) {
    var focusables = container.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    if (!focusables.length) return function () {};

    var first = focusables[0];
    var last = focusables[focusables.length - 1];

    function handle(event) {
      if (event.key !== 'Tab') return;
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    container.addEventListener('keydown', handle);
    first.focus();

    return function () {
      container.removeEventListener('keydown', handle);
    };
  }

  /* ---------------------------------------------------------------------- */
  /* En-tête collant                                                        */
  /* ---------------------------------------------------------------------- */

  function initStickyHeader() {
    var header = document.querySelector('[data-header]');
    if (!header) return;

    var sentinel = document.createElement('div');
    header.parentNode.insertBefore(sentinel, header);

    new IntersectionObserver(function (entries) {
      header.classList.toggle('is-stuck', !entries[0].isIntersecting);
    }).observe(sentinel);
  }

  /* ---------------------------------------------------------------------- */
  /* Navigation mobile                                                      */
  /* ---------------------------------------------------------------------- */

  function initMobileNav() {
    var nav = document.querySelector('[data-mobile-nav]');
    var openers = document.querySelectorAll('[data-mobile-nav-open]');
    var closers = document.querySelectorAll('[data-mobile-nav-close]');
    if (!nav || !openers.length) return;

    var releaseFocus = null;

    function open() {
      nav.classList.add('is-open');
      nav.removeAttribute('aria-hidden');
      document.body.style.overflow = 'hidden';
      releaseFocus = trapFocus(nav);
    }

    function close() {
      nav.classList.remove('is-open');
      nav.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
      if (releaseFocus) releaseFocus();
      openers[0].focus();
    }

    openers.forEach(function (el) { el.addEventListener('click', open); });
    closers.forEach(function (el) { el.addEventListener('click', close); });

    document.addEventListener('keydown', function (event) {
      if (event.key === 'Escape' && nav.classList.contains('is-open')) close();
    });
  }

  /* ---------------------------------------------------------------------- */
  /* Recherche                                                              */
  /* ---------------------------------------------------------------------- */

  function initSearch() {
    var overlay = document.querySelector('[data-search-overlay]');
    var openers = document.querySelectorAll('[data-search-open]');
    var closers = document.querySelectorAll('[data-search-close]');
    if (!overlay || !openers.length) return;

    var input = overlay.querySelector('input[type="search"]');
    var releaseFocus = null;

    function open() {
      overlay.classList.add('is-open');
      overlay.removeAttribute('aria-hidden');
      document.body.style.overflow = 'hidden';
      releaseFocus = trapFocus(overlay);
      if (input) input.focus();
    }

    function close() {
      overlay.classList.remove('is-open');
      overlay.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
      if (releaseFocus) releaseFocus();
      openers[0].focus();
    }

    openers.forEach(function (el) { el.addEventListener('click', open); });
    closers.forEach(function (el) { el.addEventListener('click', close); });

    document.addEventListener('keydown', function (event) {
      if (event.key === 'Escape' && overlay.classList.contains('is-open')) close();
    });
  }

  /* ---------------------------------------------------------------------- */
  /* Favoris (stockage local — aucune dépendance app)                        */
  /* ---------------------------------------------------------------------- */

  function readWishlist() {
    try {
      var raw = window.localStorage.getItem(WISHLIST_KEY);
      var parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      // localStorage indisponible (navigation privée, quota) : on dégrade sans casser.
      return [];
    }
  }

  function writeWishlist(handles) {
    try {
      window.localStorage.setItem(WISHLIST_KEY, JSON.stringify(handles));
    } catch (error) {
      return false;
    }
    return true;
  }

  function updateWishlistCount() {
    var badge = document.querySelector('[data-wishlist-count]');
    if (!badge) return;
    var count = readWishlist().length;
    badge.textContent = count;
    badge.hidden = count === 0;
  }

  function initWishlist() {
    var toggles = document.querySelectorAll('[data-wishlist-toggle]');
    var current = readWishlist();

    toggles.forEach(function (button) {
      var handle = button.getAttribute('data-handle');
      button.setAttribute('aria-pressed', String(current.indexOf(handle) !== -1));

      button.addEventListener('click', function (event) {
        event.preventDefault();
        event.stopPropagation();

        var list = readWishlist();
        var index = list.indexOf(handle);

        if (index === -1) {
          list.push(handle);
        } else {
          list.splice(index, 1);
        }

        if (!writeWishlist(list)) return;

        button.setAttribute('aria-pressed', String(index === -1));
        updateWishlistCount();
        document.dispatchEvent(new CustomEvent('wishlist:change', { detail: { handles: list } }));
      });
    });

    updateWishlistCount();
  }

  /* ---------------------------------------------------------------------- */
  /* Page favoris                                                           */
  /* ---------------------------------------------------------------------- */

  function initWishlistPage() {
    var container = document.querySelector('[data-wishlist-grid]');
    if (!container) return;

    var empty = document.querySelector('[data-wishlist-empty]');
    var handles = readWishlist();
    var cards = container.querySelectorAll('[data-wishlist-item]');
    var shown = 0;

    cards.forEach(function (card) {
      var visible = handles.indexOf(card.getAttribute('data-handle')) !== -1;
      card.hidden = !visible;
      if (visible) shown += 1;
    });

    container.hidden = shown === 0;
    if (empty) empty.hidden = shown > 0;
  }

  /* ---------------------------------------------------------------------- */
  /* Quantité                                                               */
  /* ---------------------------------------------------------------------- */

  function initQuantity() {
    on('[data-quantity-change]', 'click', function (event) {
      var button = event.currentTarget;
      var wrapper = button.closest('[data-quantity]');
      if (!wrapper) return;

      var input = wrapper.querySelector('input');
      var step = parseInt(button.getAttribute('data-quantity-change'), 10);
      var min = parseInt(input.getAttribute('min') || '1', 10);
      var next = (parseInt(input.value, 10) || min) + step;

      input.value = Math.max(min, next);
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }

  /* ---------------------------------------------------------------------- */
  /* Sélection de variante                                                  */
  /* ---------------------------------------------------------------------- */

  function initVariants() {
    var form = document.querySelector('[data-product-form]');
    if (!form) return;

    var dataEl = document.querySelector('[data-product-json]');
    if (!dataEl) return;

    var variants;
    try {
      variants = JSON.parse(dataEl.textContent).variants || [];
    } catch (error) {
      return;
    }

    var idInput = form.querySelector('[data-variant-id]');
    var priceEl = document.querySelector('[data-product-price]');
    var submit = form.querySelector('[data-add-to-cart]');

    function selectedOptions() {
      return Array.prototype.map.call(
        form.querySelectorAll('[data-option-index]'),
        function (group) {
          var checked = group.querySelector('input:checked');
          return checked ? checked.value : null;
        }
      );
    }

    function matchVariant(options) {
      return variants.find(function (variant) {
        return variant.options.every(function (option, index) {
          return option === options[index];
        });
      });
    }

    function update() {
      var variant = matchVariant(selectedOptions());

      if (!variant) {
        submit.setAttribute('aria-disabled', 'true');
        submit.textContent = strings.soldOut || 'Indisponible';
        return;
      }

      idInput.value = variant.id;

      if (priceEl && variant.price_html) {
        priceEl.innerHTML = variant.price_html;
      }

      submit.setAttribute('aria-disabled', String(!variant.available));
      submit.textContent = variant.available
        ? (strings.addToCart || 'Ajouter au panier')
        : (strings.soldOut || 'Épuisé');

      var url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url.toString());
    }

    on('[data-option-index] input', 'change', update, form);
  }

  /* ---------------------------------------------------------------------- */
  /* Ajout au panier                                                        */
  /* ---------------------------------------------------------------------- */

  function setCartCount(count) {
    document.querySelectorAll('[data-cart-count]').forEach(function (el) {
      el.textContent = count;
      el.hidden = count === 0;
    });
  }

  function initAddToCart() {
    var form = document.querySelector('[data-product-form]');
    if (!form) return;

    var submit = form.querySelector('[data-add-to-cart]');
    var feedback = form.querySelector('[data-cart-feedback]');

    form.addEventListener('submit', function (event) {
      if (submit.getAttribute('aria-disabled') === 'true') {
        event.preventDefault();
        return;
      }

      event.preventDefault();
      var initialLabel = submit.textContent;
      submit.setAttribute('aria-disabled', 'true');

      fetch(routes.cartAdd, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: new FormData(form)
      })
        .then(function (response) {
          if (!response.ok) throw new Error('cart-add-failed');
          return response.json();
        })
        .then(function () {
          return fetch(routes.cart + '.js', { headers: { Accept: 'application/json' } });
        })
        .then(function (response) { return response.json(); })
        .then(function (cart) {
          setCartCount(cart.item_count);
          if (feedback) {
            feedback.textContent = strings.addedToCart || 'Ajouté au panier';
            feedback.hidden = false;
            feedback.classList.remove('form-message--error');
          }
        })
        .catch(function () {
          // Échec réseau ou stock épuisé côté serveur : on le dit, on ne l'avale pas.
          if (feedback) {
            feedback.textContent = strings.error || 'Une erreur est survenue. Merci de réessayer.';
            feedback.hidden = false;
            feedback.classList.add('form-message--error');
          }
        })
        .finally(function () {
          submit.setAttribute('aria-disabled', 'false');
          submit.textContent = initialLabel;
        });
    });
  }

  /* ---------------------------------------------------------------------- */
  /* Accordéons                                                             */
  /* ---------------------------------------------------------------------- */

  function initAccordions() {
    on('[data-accordion-trigger]', 'click', function (event) {
      var trigger = event.currentTarget;
      var expanded = trigger.getAttribute('aria-expanded') === 'true';
      var panel = document.getElementById(trigger.getAttribute('aria-controls'));

      trigger.setAttribute('aria-expanded', String(!expanded));
      if (panel) panel.hidden = expanded;
    });
  }

  /* ---------------------------------------------------------------------- */
  /* Galerie produit                                                        */
  /* ---------------------------------------------------------------------- */

  function initGallery() {
    var main = document.querySelector('[data-gallery-main]');
    if (!main) return;

    on('[data-gallery-thumb]', 'click', function (event) {
      var thumb = event.currentTarget;

      main.src = thumb.getAttribute('data-full');
      main.srcset = thumb.getAttribute('data-srcset') || '';
      main.alt = thumb.getAttribute('data-alt') || '';

      document.querySelectorAll('[data-gallery-thumb]').forEach(function (el) {
        el.setAttribute('aria-current', String(el === thumb));
      });
    });
  }

  /* ---------------------------------------------------------------------- */
  /* Révélation au scroll                                                   */
  /* ---------------------------------------------------------------------- */

  function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function initReveal() {
    var targets = document.querySelectorAll('.reveal, .reveal-children');
    if (!targets.length) return;

    if (prefersReducedMotion()) {
      targets.forEach(function (el) { el.classList.add('is-visible'); });
      return;
    }

    // Décalage en cascade, plafonné : au-delà d'une demi-seconde l'attente
    // se voit plus qu'elle n'accompagne.
    var STEP = 70;
    var MAX_DELAY = 490;

    document.querySelectorAll('.reveal-children').forEach(function (group) {
      Array.prototype.forEach.call(group.children, function (child, index) {
        child.style.transitionDelay = Math.min(index * STEP, MAX_DELAY) + 'ms';
      });
    });

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -12% 0px', threshold: 0.05 }
    );

    targets.forEach(function (el) { observer.observe(el); });
  }

  /* ---------------------------------------------------------------------- */
  /* Parallaxe                                                              */
  /* ---------------------------------------------------------------------- */

  function initParallax() {
    var targets = document.querySelectorAll('[data-parallax]');
    if (!targets.length || prefersReducedMotion()) return;

    // Agrandissement compensant l'amplitude du déplacement : sans lui, une
    // bande vide apparaîtrait en haut ou en bas du cadre.
    var SCALE = 1.12;
    var visible = [];
    var ticking = false;

    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        var index = visible.indexOf(entry.target);
        if (entry.isIntersecting && index === -1) {
          visible.push(entry.target);
        } else if (!entry.isIntersecting && index !== -1) {
          visible.splice(index, 1);
        }
      });
      request();
    });

    function place(el) {
      var strength = parseFloat(el.getAttribute('data-parallax')) || 0.12;
      var rect = el.getBoundingClientRect();

      // Position du centre de l'élément par rapport au centre de l'écran,
      // ramenée dans [-1, 1].
      var elementCenter = rect.top + rect.height / 2;
      var viewportCenter = window.innerHeight / 2;
      var progress = (elementCenter - viewportCenter) / window.innerHeight;
      progress = Math.max(-1, Math.min(1, progress));

      var shift = progress * rect.height * strength;
      el.style.transform = 'translate3d(0, ' + shift.toFixed(2) + 'px, 0) scale(' + SCALE + ')';
    }

    function update() {
      visible.forEach(place);
      ticking = false;
    }

    function request() {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(update);
    }

    targets.forEach(function (el) { observer.observe(el); });
    window.addEventListener('scroll', request, { passive: true });
    window.addEventListener('resize', request, { passive: true });
    request();
  }

  /* ---------------------------------------------------------------------- */
  /* Amorçage                                                               */
  /* ---------------------------------------------------------------------- */

  function init() {
    initStickyHeader();
    initMobileNav();
    initSearch();
    initWishlist();
    initWishlistPage();
    initQuantity();
    initVariants();
    initAddToCart();
    initAccordions();
    initGallery();
    initReveal();
    initParallax();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Réinitialisation après édition dans le personnalisateur de thème.
  document.addEventListener('shopify:section:load', init);
})();
